import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import {
  BB_LETTERS,
  BB_LETTER_TO_SECTION,
  type BbLetter,
  type SectionId,
} from "@/lib/bbSectionMap";

const isBbLetter = (value: string | null): value is BbLetter =>
  Boolean(
    value && Object.prototype.hasOwnProperty.call(BB_LETTER_TO_SECTION, value),
  );

const letterForSection = (section: SectionId): BbLetter =>
  BB_LETTERS.find((letter) => BB_LETTER_TO_SECTION[letter] === section) ?? "A";

export function useBrandBibleSection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("section");
  const activeLetter: BbLetter = isBbLetter(requested) ? requested : "A";
  const activeSection = BB_LETTER_TO_SECTION[activeLetter];

  const setActiveLetter = useCallback(
    (letter: BbLetter) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          next.set("section", letter);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setActiveSection = useCallback(
    (section: SectionId) => setActiveLetter(letterForSection(section)),
    [setActiveLetter],
  );

  return { activeLetter, activeSection, setActiveLetter, setActiveSection };
}

/** Keep every editor mounted; only the native hidden state changes. */
export function sectionVisibilityProps(active: SectionId, section: SectionId) {
  const visible = active === section;
  return {
    hidden: !visible,
    tabIndex: -1,
    "data-bb-section": letterForSection(section),
    "data-testid": visible ? "bb-section-viewer-body" : undefined,
  } as const;
}
